/**
 * Socket.IO Client Utility for Chat and Notifications
 * Handles real-time chat and notification connections
 */

import { io } from 'socket.io-client';
import { getToken } from '../api/endpoints';
import { AUTH_CONFIG, SOCKET_BASE_URL } from '../config/constants';

/**
 * Shared Socket.IO client options aligned with backend (server.js):
 * - path defaults to /socket.io
 * - polling first (nginx often blocks WS upgrade; backend allows both)
 * - auth.token matches authenticateSocket middleware
 */
export const getDefaultSocketOptions = (token, options = {}) => ({
  path: '/socket.io',
  // Match backend preference: polling works when WS upgrade fails
  transports: ['polling', 'websocket'],
  upgrade: true,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: Infinity,
  withCredentials: false,
  auth: { token },
  ...options,
});

/**
 * Create and configure Socket.IO connection for chat
 * @param {Object} options - Connection options
 * @returns {Socket} - Socket.IO instance
 */
export const createChatSocket = (options = {}) => {
  const baseURL = SOCKET_BASE_URL;
  const token = getToken() || localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
  
  // Try root namespace first (like MePage does)
  const socket = io(baseURL, getDefaultSocketOptions(token, options));

  socket.on('connect', () => {
    console.log('🔌 Connected to chat server', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('🔌 Disconnected from chat server:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('❌ Chat connection error:', error);
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('🔄 Reconnected to chat server after', attemptNumber, 'attempts');
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    console.log('🔄 Reconnection attempt', attemptNumber);
  });

  return socket;
};

// Singleton socket instance for notifications (shared across components)
let notificationSocketInstance = null;

/**
 * Create and configure Socket.IO connection for notifications
 * Uses a singleton pattern to ensure only one socket connection
 * @param {Object} options - Connection options
 * @returns {Socket} - Socket.IO instance
 */
export const createNotificationSocket = (options = {}) => {
  // Return existing socket if available and connected
  if (notificationSocketInstance && notificationSocketInstance.connected) {
    console.log('♻️ Reusing existing notification socket');
    return notificationSocketInstance;
  }

  // Create new socket connection
  const baseURL = SOCKET_BASE_URL;
  const token = getToken() || localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
  
  const socket = io(baseURL, getDefaultSocketOptions(token, options));

  socket.on('connect', () => {
    console.log('🔔 Notification socket connected:', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('🔔 Notification socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('❌ Notification socket connection error:', error);
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('🔄 Notification socket reconnected after', attemptNumber, 'attempts');
  });

  notificationSocketInstance = socket;
  return socket;
};

/**
 * Get the current notification socket instance
 * @returns {Socket|null} - Current socket instance or null
 */
export const getNotificationSocket = () => {
  return notificationSocketInstance;
};

/**
 * Disconnect notification socket
 */
export const disconnectNotificationSocket = () => {
  if (notificationSocketInstance) {
    notificationSocketInstance.disconnect();
    notificationSocketInstance = null;
  }
};

/**
 * Get user ID from localStorage
 * @returns {string|null} - User ID or null
 */
export const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY) || '{}');
    return user.id || user.userId || user._id || null;
  } catch {
    return null;
  }
};

/**
 * Socket event names for chat
 * Aligned with chat-demo.html: join-chat-group, leave-chat-group, new-message, message-deleted
 */
export const CHAT_EVENTS = {
  // Client emits (chat-demo style)
  JOIN_CHAT_GROUP: 'join-chat-group',
  LEAVE_CHAT_GROUP: 'leave-chat-group',
  TYPING: 'typing',
  // Legacy
  JOIN_GROUP: 'join-group',
  LEAVE_GROUP: 'leave-group',
  
  // Server emits (chat-demo style)
  NEW_MESSAGE: 'new-message',
  MESSAGE_DELETED: 'message-deleted',
  USER_TYPING: 'user-typing',
  JOINED_CHAT_GROUP: 'joined-chat-group',
  MESSAGE_DELETE_SUCCESS: 'message-delete-success',
  MESSAGE_UPDATED: 'message-updated',
  MESSAGE_PINNED: 'message-pinned',
  MESSAGE_UNPINNED: 'message-unpinned',
  USER_JOINED: 'user-joined',
  USER_LEFT: 'user-left',
  ERROR: 'error'
};

/**
 * Socket event names for notifications
 */
export const NOTIFICATION_EVENTS = {
  // Client emits
  JOIN_NOTIFICATIONS: 'join-notifications',
  LEAVE_NOTIFICATIONS: 'leave-notifications',
  MARK_NOTIFICATION_READ: 'mark-notification-read',
  
  // Server emits
  JOINED_NOTIFICATIONS: 'joined-notifications',
  LEFT_NOTIFICATIONS: 'left-notifications',
  NEW_NOTIFICATION: 'newNotification', // Backend uses camelCase
  NOTIFICATION_MARKED_READ: 'notification-marked-read',
  ERROR: 'error'
};

export default createChatSocket;
